import { formatDateRangeShort, ymdToDate } from "../../lib/dates";
import type { PlannedWorkout } from "../../lib/plannerTypes";
import type { WizardStepId } from "./wizard/wizardMeta";

/**
 * Wave 3 (§10 Wave 3 · mock 1g) — pure builders for the no-race path's REVIEW
 * (the step that builds the first box of a rolling journey): the training
 * summary chip + guardrail-5 warnings and the four recap rows. Kept out of
 * `TrainingPlanWizard` so the copy math is unit-testable without rendering the
 * whole wizard. The race path keeps its own recap.
 */

/** The collapsed training-summary the host hands the wizard (from the workbench's
 *  loaded upcoming workouts). Undefined counts / range are honest "we don't know
 *  yet" states, not zero. */
export interface PlannedSummary {
  /** Upcoming (today-onward) planned sessions the workbench has loaded. */
  sessionCount: number;
  /** "Jul 26 – Nov 14" over the upcoming sessions, or null when none. */
  dateRange: string | null;
  /** How many of those sessions have no duration set (drives a warning). */
  missingDurationCount: number;
}

/**
 * The ONE derivation of a `PlannedSummary` from planned workouts: upcoming =
 * dated today or later and not excluded from planning. Shared by the
 * workbench (its loaded calendar) and the wizard's own bounded fetch.
 */
export function summarizePlannedWorkouts(
  workouts: readonly Pick<PlannedWorkout, "date" | "durationMin" | "excludedFromPlanning">[],
  todayYmd: string
): PlannedSummary {
  const upcoming = workouts.filter((w) => !w.excludedFromPlanning && w.date >= todayYmd);
  if (upcoming.length === 0) return { sessionCount: 0, dateRange: null, missingDurationCount: 0 };
  const dates = upcoming.map((w) => w.date).sort();
  return {
    sessionCount: upcoming.length,
    dateRange: formatDateRangeShort(ymdToDate(dates[0]), ymdToDate(dates[dates.length - 1])),
    missingDurationCount: upcoming.filter((w) => w.durationMin == null || w.durationMin <= 0)
      .length
  };
}

/** One auto-expand warning row (guardrail 5): the summary opens into these
 *  whenever the data warrants, so an unfuelable calendar is never silent. */
export interface SummaryWarning {
  key: "no-sessions" | "missing-duration";
  text: string;
}

/** The training summary chip + any warnings it must expand into. */
export interface IntroSummaryModel {
  /** "12 sessions · Jul 26 – Nov 14", or a bare "No upcoming sessions yet". */
  chip: string;
  /** Non-empty ⇒ the summary auto-expands into a warning list (guardrail 5). */
  warnings: SummaryWarning[];
}

/**
 * Build the training summary chip + warnings from the loaded planned workouts.
 * `null` summary (host hasn't computed one) still yields a usable chip.
 */
export function computeIntroSummary(summary: PlannedSummary | null): IntroSummaryModel {
  const sessionCount = summary?.sessionCount ?? 0;
  const dateRange = summary?.dateRange ?? null;
  const missing = summary?.missingDurationCount ?? 0;

  const warnings: SummaryWarning[] = [];
  if (sessionCount === 0) {
    warnings.push({
      key: "no-sessions",
      text: "No upcoming sessions on your calendar yet — add some and we'll size your fuel to them."
    });
  } else if (missing > 0) {
    warnings.push({
      key: "missing-duration",
      text:
        missing === 1
          ? "1 workout has no duration — we'll assume 45 min."
          : `${missing} workouts have no duration — we'll assume 45 min.`
    });
  }

  const chip =
    sessionCount === 0
      ? "No upcoming sessions yet"
      : `${sessionCount} session${sessionCount === 1 ? "" : "s"}${
          dateRange ? ` · ${dateRange}` : ""
        }`;

  return { chip, warnings };
}

/**
 * The four review rows (mock 1g). Real values, skipped/unset cases handled
 * gracefully ("average sodium (safe default)"). Each row's `stepId` jumps the
 * Edit link back to the owning wizard step.
 */
export interface ReviewRow {
  label: "TRAINING" | "ABOUT YOU" | "FUELING" | "BOXES";
  value: string;
  /** The wizard step the Edit link returns to. */
  stepId: WizardStepId;
}

export interface ReviewInputs {
  /** From the training summary (may be a "No upcoming sessions yet" state). */
  summaryChip: string;
  /** kg from physiology (already converted), or null when unset. */
  weightKg: number | null;
  allergenCount: number;
  /** "unsure" ⇒ the average-sodium safe default. */
  sweatUnsure: boolean;
  /** Human sodium label for a chosen level, e.g. "average sodium" / "high sodium". */
  sodiumLabel: string;
  gutLevel: "new" | "tuning" | "locked" | null;
  rampEnabled: boolean;
  /** Box cadence in weeks. No count: rolling boxes open on workouts. */
  dropWeeks: number;
}

/** "new"/"tuning"/"locked" → the recap's fuel-experience phrase. */
export function gutLevelReviewLabel(gutLevel: ReviewInputs["gutLevel"]): string {
  switch (gutLevel) {
    case "new":
      return "New to fueling";
    case "tuning":
      return "Fine-tuning my fueling";
    case "locked":
      return "Locked-in carb target";
    default:
      return "Fueling experience not set";
  }
}

export function buildReviewRows(input: ReviewInputs): ReviewRow[] {
  const aboutParts: string[] = [];
  if (input.weightKg != null) aboutParts.push(`${input.weightKg} kg`);
  aboutParts.push(
    input.allergenCount > 0
      ? `${input.allergenCount} allergen${input.allergenCount === 1 ? "" : "s"}`
      : "no allergens set"
  );
  aboutParts.push(input.sweatUnsure ? "average sodium (safe default)" : input.sodiumLabel);

  const fuelParts = [gutLevelReviewLabel(input.gutLevel)];
  fuelParts.push(input.rampEnabled ? "starting gradually" : "full targets from day 1");

  const cadence = input.dropWeeks === 1 ? "week" : `${input.dropWeeks} weeks`;
  const boxesValue = `Every ${cadence} · each bought separately`;

  return [
    { label: "TRAINING", value: input.summaryChip, stepId: "method" },
    { label: "ABOUT YOU", value: aboutParts.join(" · "), stepId: "body" },
    { label: "FUELING", value: fuelParts.join(" · "), stepId: "fuel" },
    { label: "BOXES", value: boxesValue, stepId: "cadence" }
  ];
}
